import { useCallback, useRef, useState } from 'react'
import MConfirm from '@/utils/modal/MConfirm'
import MToast from '@/utils/modal/MToast'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { useOperatorReplan } from '@/services/outbound/outboundUnallocated/useOutboundUnallocatedQuries'
import {
  classifyOperatorReplanFailure,
  OPERATOR_REPLAN_ACCEPTED_MESSAGE,
  OPERATOR_REPLAN_BUTTON_TEXT,
  OPERATOR_REPLAN_CONFIRM_MESSAGE,
  OPERATOR_REPLAN_CONFIRM_SUB_MESSAGE,
  OPERATOR_REPLAN_DISABLED_MESSAGE,
  OPERATOR_REPLAN_EMPTY_MESSAGE,
  OPERATOR_REPLAN_FAILED_MESSAGE,
  OPERATOR_REPLAN_FLAG,
  OPERATOR_REPLAN_NOT_OPTIMIZER_TARGET_MESSAGE,
  OPERATOR_REPLAN_NOT_UNALLOCATED_MESSAGE,
  OPERATOR_REPLAN_REQUEST_FAILED_MESSAGE,
  OPERATOR_REPLAN_UNAVAILABLE_MESSAGE,
  OPERATOR_REPLAN_UNCONFIRMED_MESSAGE,
  OPERATOR_REPLAN_UNSENT_MESSAGE,
  OperatorReplanFailure,
  OperatorReplanItemResult,
  ReplanTargetRow,
  toOperatorReplanReqs,
} from '@/services/outbound/outboundUnallocated/replan'

interface UseUnallocatedReplanActionParams {
  /** 접수 후 목록을 다시 읽는다. 조회 훅이 `enabled: false` 라 invalidate 가 아니라 refetch 다. */
  onDone?: () => void
}

interface UnallocatedReplanAction {
  enabled: boolean
  isPending: boolean
  /** 확인 창을 띄우는 데까지가 동기다 — 실제 요청은 사용자가 확인을 누른 뒤에 나간다. */
  requestReplan: (rows: ReplanTargetRow[], onDeselect?: () => void) => void
}

/**
 * 미할당 행 출고지시 — 선택 행을 화주별로 묶고 요청당 상한 단위로 나눠 **전부 순차로** 보낸다(화주 간에도 병렬 없음).
 * 응답이 온 묶음은 건별 실패가 있어도 다음 묶음을 계속 보내고, 요청 자체가 reject 되면(종류 무관) 즉시 멈춰
 * 남은 묶음은 보내지 않는다. 재시도·복구는 하지 않으며, 끝난 뒤 건수를 합산해 토스트로 알린다.
 */
export const useUnallocatedReplanAction = ({ onDone }: UseUnallocatedReplanActionParams): UnallocatedReplanAction => {
  const { data: enabled = false } = useFeatureFlag(OPERATOR_REPLAN_FLAG)
  const { mutateAsync } = useOperatorReplan()
  const [isPending, setIsPending] = useState(false)
  const inFlightRef = useRef(false)

  const requestReplan = useCallback(
    (rows: ReplanTargetRow[], onDeselect?: () => void) => {
      if (!enabled) return

      if (!rows.length) {
        MToast.error(OPERATOR_REPLAN_EMPTY_MESSAGE)
        return
      }

      const requests = toOperatorReplanReqs(rows)

      MConfirm.show(OPERATOR_REPLAN_CONFIRM_MESSAGE(rows.length), {
        subContent: OPERATOR_REPLAN_CONFIRM_SUB_MESSAGE,
        confirmText: OPERATOR_REPLAN_BUTTON_TEXT,
        closeText: '취소',
        onConfirm: async () => {
          // 확인 버튼 연타·재호출로 같은 선택이 두 벌 나가지 않도록 진행 중이면 즉시 돌려보낸다. state 는 비동기라 ref 로 막는다.
          if (inFlightRef.current) return
          inFlightRef.current = true
          setIsPending(true)
          try {
            const results: OperatorReplanItemResult[] = []
            let hasResponse = false
            let stopped: { failure: OperatorReplanFailure; count: number; unsent: number } | undefined

            for (let index = 0; index < requests.length; index += 1) {
              const request = requests[index]
              try {
                // 순차 호출이 요구사항이다 — 서버 부하와 중단 규칙(실패 시 남은 묶음 미전송) 때문에 병렬로 바꾸지 않는다.
                // eslint-disable-next-line no-await-in-loop
                const { data } = await mutateAsync(request)
                results.push(...data.results)
                hasResponse = true
              } catch (error) {
                // 요청이 reject 되면 이어서 보내지 않는다 — 같은 원인으로 줄줄이 실패하거나 접수 여부가 더 꼬일 수 있다.
                const unsent = requests.slice(index + 1).reduce((sum, { items }) => sum + items.length, 0)
                stopped = { failure: classifyOperatorReplanFailure(error), count: request.items.length, unsent }
                break
              }
            }

            if (hasResponse) {
              onDeselect?.()
              onDone?.()
            }

            const accepted = results.filter(({ status }) => status === 'ACCEPTED').length
            if (accepted) {
              MToast.show(OPERATOR_REPLAN_ACCEPTED_MESSAGE(rows.length, accepted))
            }

            const notOptimizerTarget = results.filter(({ status }) => status === 'NOT_OPTIMIZER_TARGET').length
            if (notOptimizerTarget) {
              MToast.error(OPERATOR_REPLAN_NOT_OPTIMIZER_TARGET_MESSAGE(rows.length, notOptimizerTarget))
            }

            const notUnallocated = results.filter(({ status }) => status === 'NOT_UNALLOCATED').length
            if (notUnallocated) {
              MToast.error(OPERATOR_REPLAN_NOT_UNALLOCATED_MESSAGE(rows.length, notUnallocated))
            }

            const failedMessages = results.filter(({ status }) => status === 'FAILED').map(({ message }) => message)
            if (failedMessages.length) {
              MToast.error(OPERATOR_REPLAN_FAILED_MESSAGE(rows.length, failedMessages))
            }

            if (stopped) {
              const { failure, count, unsent } = stopped
              if (failure.kind === 'DISABLED') {
                // 상류 flag OFF 는 재시도가 무의미하다 — 미전송분도 같은 사유이므로 비활성 건수에 합쳐 한 장으로 알린다.
                MToast.error(OPERATOR_REPLAN_DISABLED_MESSAGE(rows.length, count + unsent))
              } else {
                switch (failure.kind) {
                  case 'UNAVAILABLE':
                    MToast.error(OPERATOR_REPLAN_UNAVAILABLE_MESSAGE(rows.length, count))
                    break
                  case 'UNCONFIRMED':
                    MToast.error(OPERATOR_REPLAN_UNCONFIRMED_MESSAGE(rows.length, count))
                    break
                  default:
                    MToast.error(OPERATOR_REPLAN_REQUEST_FAILED_MESSAGE(rows.length, count))
                }
                if (unsent) {
                  MToast.error(OPERATOR_REPLAN_UNSENT_MESSAGE(rows.length, unsent))
                }
              }
            }
          } finally {
            inFlightRef.current = false
            setIsPending(false)
          }
        },
      })
    },
    [enabled, mutateAsync, onDone],
  )

  return { enabled, isPending, requestReplan }
}
